/**
 * routes.jsx — CPG Agreements routes, ready to drop into App.js.
 *
 * In App.js:
 *   import { AgreementsRoutes } from "@/pages/agreements/routes";
 *   ...
 *   <Routes>
 *     ...existing routes...
 *     {AgreementsRoutes({ guard: Protected })}
 *   </Routes>
 *
 * Route map
 *   /agreements                     Dashboard
 *   /agreements/library             Filterable document library (257 entries)
 *   /agreements/library/:docId      One document entry
 *   /agreements/templates           Every draftable template, grouped by topic
 *   /agreements/finder              "What agreement do I need?"
 *   /agreements/intake              Project / brand intake wizard
 *   /agreements/plan                The generated document plan
 *   /agreements/checklist           The CPG Agreement Master Checklist
 *   /agreements/bundles             Pre-built bundles
 *   /agreements/bundles/:bundleId   One bundle
 *   /agreements/roadmap             The CPG legal roadmap
 *   /agreements/red-flags/:listId   Red-flag checklists
 *   /agreements/template/:docId     A template + its Founder Guide
 */
import React from "react";
import { Route, Outlet, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { AgreementProjectProvider } from "@/hooks/useAgreementProject";
import AgreementsDashboard from "./AgreementsDashboard";
import DocumentLibrary from "./DocumentLibrary";
import DocumentDetail from "./DocumentDetail";
import AgreementFinder from "./AgreementFinder";
import ProjectIntake from "./ProjectIntake";
import DocumentPlan from "./DocumentPlan";
import MasterChecklist from "./MasterChecklist";
import Bundles from "./Bundles";
import BundleDetail from "./BundleDetail";
import LegalRoadmap from "./LegalRoadmap";
import RedFlagChecklist from "./RedFlagChecklist";
import TemplateView from "./TemplateView";
import Templates from "./Templates";

function AgreementsLayout() {
  // Render every CPG Agreements page inside the app's main <AppShell>
  // (sidebar + header) — mirrors the AI CPG Tools pattern.
  //
  // Multi-project support: the active project id is driven by the URL
  // `?project=<id>` query parameter so each intake submission stays a
  // separate record. When absent (e.g. legacy links), we fall back to
  // "default" — matches historical behaviour so nothing existing
  // silently changes. `key={projectId}` remounts the provider (and
  // therefore its `useEffect` fetch) whenever the URL project changes,
  // so switching brands doesn't stream stale answers from React state
  // into the next form.
  const [params] = useSearchParams();
  const projectId = params.get("project") || "default";
  return (
    <AppShell>
      <AgreementProjectProvider key={projectId} projectId={projectId}>
        <Outlet />
      </AgreementProjectProvider>
    </AppShell>
  );
}

export function AgreementsRoutes({ guard: Guard } = {}) {
  const layout = <AgreementsLayout />;
  const element = Guard ? <Guard>{layout}</Guard> : layout;
  return (
    <Route path="/agreements" element={element}>
      <Route index element={<AgreementsDashboard />} />
      <Route path="library" element={<DocumentLibrary />} />
      <Route path="library/:docId" element={<DocumentDetail />} />
      <Route path="templates" element={<Templates />} />
      <Route path="finder" element={<AgreementFinder />} />
      <Route path="intake" element={<ProjectIntake />} />
      <Route path="plan" element={<DocumentPlan />} />
      <Route path="checklist" element={<MasterChecklist />} />
      <Route path="bundles" element={<Bundles />} />
      <Route path="bundles/:bundleId" element={<BundleDetail />} />
      <Route path="roadmap" element={<LegalRoadmap />} />
      <Route path="red-flags/:listId" element={<RedFlagChecklist />} />
      <Route path="template/:docId" element={<TemplateView />} />
    </Route>
  );
}

export {
  AgreementsDashboard,
  DocumentLibrary,
  DocumentDetail,
  AgreementFinder,
  ProjectIntake,
  DocumentPlan,
  MasterChecklist,
  Bundles,
  BundleDetail,
  LegalRoadmap,
  RedFlagChecklist,
  TemplateView,
  Templates,
  AgreementsLayout,
};
